// --- Dependencies -------------------------------------------------------------------------------
import { useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import useLang from "@/hooks/useLang";
import usePopup from "@/hooks/usePopup";
import useTranslation from "@/hooks/useTranslation";
import type { TyEnquiry, TyEnquiryStatus } from "@/types";
import { formatDateTime } from "@/utils";
import Avatar from "@/UI/atoms/Avatar";
import Icon from "@/UI/atoms/Icon";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";
import "./styles.css";
// --- Types --------------------------------------------------------------------------------------
export type TyEnquiryDetails = {
    enquiry: TyEnquiry;
    /** The list behind this panel reloads on it — the status is written the moment it is confirmed. */
    onStatus: (status: TyEnquiryStatus) => void;
    /** Starts the company form, filled in with what they wrote. */
    onConvert: () => void;
};

type Props = { onClose: () => void; details?: unknown };
// --- Logic --------------------------------------------------------------------------------------
/**
 * One enquiry, whole: who asked, what they wrote, and the two things to do about it — move it along
 * or turn it into a company.
 *
 * **There is nothing to save here.** What the visitor typed is theirs and is never edited, and the
 * status is written the moment its confirmation is accepted, because it is also moved from the tile
 * and the two must mean the same thing. A panel with a save button would be a panel implying there
 * is something in it we own.
 */
const FLOW = constants.enquiryStatuses as readonly TyEnquiryStatus[];

export default function EnquiryPopup({ onClose, details }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";
    const addComp = usePopup((state) => state.addComp);
    const { enquiry, onStatus, onConvert } = (details ?? {}) as TyEnquiryDetails;

    /** The list reloads behind this panel, but the enquiry it was opened with does not. */
    const [status, setStatus] = useState<TyEnquiryStatus>(enquiry.status);

    /** Every move asks first: the status is what the queue is filtered on, and a mis-aimed press
     *  takes an enquiry out of the list nobody then looks at again. */
    const pick = (next: TyEnquiryStatus) => {
        if (next === status) return;
        addComp({
            name: "confirm",
            Component: ConfirmPopup,
            anim: "scale",
            details: {
                title: t.enquiries.moveStatus,
                message: t.enquiries.confirmStatus.replace("{status}", t.enquiries.statuses[next]),
                confirmLabel: t.enquiries.moveStatus,
                danger: false,
                onConfirm: () => {
                    setStatus(next);
                    onStatus(next);
                },
            },
        });
    };

    return (
        <section className="side-form side-form--center">
            <header className="side-form__header">
                <h2 className="side-form__title">{t.enquiries.detailsTitle}</h2>
                <button type="button" className="side-form__close" onClick={onClose} aria-label={t.common.close}>
                    <Icon name="close" size={16} />
                </button>
            </header>

            <div className="side-form__body">
                {/* Who asked, and the one way back to them. There is no other channel. */}
                <div className="enquiry__who">
                    <Avatar name={enquiry.organizationName} size={44} bordered rounded />

                    <div className="enquiry__who-body">
                        <span className="enquiry__who-company">{enquiry.organizationName}</span>
                        <span className="enquiry__who-person">{enquiry.clientName}</span>
                    </div>

                    <a className="enquiry__call" href={`tel:${enquiry.phone}`}>
                        <Icon name="phone" size={16} />
                        <bdi dir="ltr">{enquiry.phone}</bdi>
                    </a>
                </div>

                <div className="enquiry__states">
                    {FLOW.map((step) => (
                        <button key={step} type="button" className="enquiry__state" data-on={status === step} onClick={() => pick(step)}>
                            {t.enquiries.statuses[step]}
                        </button>
                    ))}
                </div>

                {/* Their words, read-only, always. */}
                <div className="enquiry__said">
                    <span className="enquiry__when">{formatDateTime(enquiry.createdAt, lang)}</span>
                    <p className="enquiry__message">{enquiry.message || t.enquiries.noMessage}</p>
                </div>

                <button type="button" className="enquiry__convert" onClick={onConvert}>
                    <Icon name="add" size={16} />
                    <span>{t.enquiries.convert}</span>
                    <small>{t.enquiries.convertHint}</small>
                </button>
            </div>
        </section>
    );
}
